import { apiClient } from "./apiClient";

/** P5: the budget-push approval queue (operator-only; flag-gated 503 when
 *  PLANNER_BUDGET_PUSH_ENABLED is off). */

export interface BudgetPushJob {
  id: string;
  contract_id: string;
  contract_name: string;
  platform: string;
  cycle_start: string;
  cycle_end: string;
  status:
    | "queued"
    | "approved"
    | "pushing"
    | "pushed"
    | "failed"
    | "cancelled"
    | "skipped";
  computed_daily_dollars: number | null;
  capped_daily_dollars: number | null;
  cap_reason: string | null;
  allocation_kind: string | null;
  allocation_reason: string | null;
  error: string | null;
  trigger_source: "cycle_reset" | "routine" | "manual" | "retry";
  created_at: string;
  approved_at: string | null;
  pushed_at: string | null;
}

export interface PushPreviewCampaign {
  campaign_id: string;
  campaign_name: string | null;
  campaign_status: string | null;
  current_daily_dollars: number;
  historical_spend_dollars: number;
  proposed_daily_dollars: number | null;
}

export interface PushPreview {
  kind: string;
  reason: string | null;
  effective_daily_total: number;
  campaigns: PushPreviewCampaign[];
}

export const budgetPushService = {
  async listJobs(statuses?: string[]): Promise<BudgetPushJob[]> {
    const { data } = await apiClient.get<BudgetPushJob[]>(
      "/google-ads/budget-push/jobs",
      { params: { status: (statuses ?? []).join(",") } },
    );
    return data;
  },

  /** The webapp's Approve button: approve + push, synchronously. */
  async approve(jobId: string): Promise<{ approved: boolean; status: string; error?: string }> {
    const { data } = await apiClient.post(
      `/google-ads/budget-push/jobs/${jobId}/approve`,
    );
    return data;
  },

  async cancel(jobId: string): Promise<{ ok: boolean }> {
    const { data } = await apiClient.post(
      `/google-ads/budget-push/jobs/${jobId}/cancel`,
    );
    return data;
  },

  async preview(jobId: string): Promise<PushPreview> {
    const { data } = await apiClient.get<PushPreview>(
      `/google-ads/budget-push/jobs/${jobId}/preview`,
    );
    return data;
  },

  async enqueue(contractId?: string, resetExisting = false) {
    const { data } = await apiClient.post("/google-ads/budget-push/enqueue", null, {
      params: { contract_id: contractId ?? "", reset_existing: resetExisting },
    });
    return data;
  },

  async cadence(): Promise<{ push_cadence: string }> {
    const { data } = await apiClient.get("/google-ads/budget-push/cadence");
    return data;
  },
};
